// Drag-mousedown/move/up logic for UmlShapeWidgetBase, extracted (2026-07-09)
// to keep the base class itself small. Pure functions operating on a widget
// instance (widget.props, widget._dragOffset, widget.logDebug, etc.) — not a
// class of their own since they need direct access to the widget's mutable
// drag-session fields (_dragOffset, _dragStart, _dragConfirmed, _mouseDownTarget).
import umlCanvasEngine from '../../Core/UmlCanvasEngine';
import userSettingsEngine from '../../../controllers/UserSettings/UserSettingsEngine';
import CanvasGeometry from './CanvasGeometry.js';

// Drag threshold (2026-07-09): without this, ANY mouse movement during
// mousedown moves the shape, including a couple pixels of hand tremor between
// the two clicks of a double-click or during a plain click. Below this many
// SCREEN pixels from the mousedown position, it still counts as a click, not a
// drag — measured in screen pixels (not world units), since it's about actual
// physical hand tremor, unaffected by zoom.
const DRAG_THRESHOLD = 4;

// Keeps a shape from being dragged to a negative canvas/world coordinate.
// No upper bound — the canvas is an unbounded, pannable/zoomable world, so
// clamping against window.innerWidth/innerHeight (as this used to) compared a
// world coordinate against a SCREEN pixel count, which is meaningless once the
// user pans or zooms (bug found 2026-07-10, same root cause as the missing
// toWorldPoint conversion below).
function clampCoord(value) {
  return Math.max(0, value);
}

export function handleDragMouseDownHelper(widget, e) {
  if (e.button !== 0) return;
  if (e.target.closest('.uml-resize-handle')) return;

  const shape = widget.props.shape;
  widget._mouseDownTarget = e.target; // read on mouseup to detect a plain click, see onDragMouseUpHelper

  // Shift+click toggles multi-selection membership instead of starting a drag —
  // Ctrl/Cmd is already taken below for drawing a connector.
  if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
    e.stopPropagation();
    widget.props.onShapeShiftSelected?.(shape.id);
    return;
  }

  // Select on ANY mousedown on the shape (2026-07-11) — was previously ONLY
  // wired through handlePlainClick's name-zone check, so clicking the body/
  // members never selected anything (bug: toolbar's "Selected" row stayed
  // permanently empty, only hover-tracking worked). Selection now fires
  // immediately at mousedown, regardless of what happens after (click, drag,
  // ctrl+drag-to-connect) — same as Visio/Figma/most editors.
  if (widget.props.onShapeSelected) {
    widget.props.onShapeSelected(shape.id);
  }

  widget.logDebug('handleDragMouseDown', `ctrlKey=${e.ctrlKey} metaKey=${e.metaKey} hasOnConnectorDragStart=${Boolean(widget.props.onConnectorDragStart)} shapeId=${shape.id}`);

  // Ctrl(/Cmd on Mac)+mousedown = draw a connector instead of moving the shape —
  // same pattern as Visio/PowerPoint. Fully delegated to UmlCanvas.jsx
  // (onConnectorDragStart), which handles the temporary line + hit-test +
  // UmlConnector creation. No own mousemove/mouseup listeners needed here.
  if ((e.ctrlKey || e.metaKey) && widget.props.onConnectorDragStart) {
    e.preventDefault();
    e.stopPropagation();
    widget.props.onConnectorDragStart(shape.id, e);
    return;
  }

  e.preventDefault();

  // Offset is kept in WORLD units (via toWorldPoint), not raw screen pixels —
  // shape.x/y are world coordinates, so mixing them with e.clientX/clientY
  // directly (as this used to) silently broke as soon as the canvas was
  // panned or zoomed (bug found 2026-07-10: dragged shapes ended up at the
  // wrong world position, which in turn broke package-containment checks
  // downstream in UmlCanvasEngine, since those compare world coordinates).
  const startWorld = widget.props.toWorldPoint(e.clientX, e.clientY);
  widget._dragOffset = { x: startWorld.x - shape.x, y: startWorld.y - shape.y };
  widget._dragStart = { x: e.clientX, y: e.clientY }; // screen coords — only used for the drag-threshold check above
  widget._dragConfirmed = false;

  if (widget.props.onBringToFront) {
    widget.props.onBringToFront(shape.id);
  }

  document.body.style.userSelect = 'none';
  document.body.classList.add('jx-dragging');
  document.body.style.cursor = 'grabbing';

  window.addEventListener('mousemove', widget._onDragMouseMove, true);
  window.addEventListener('mouseup', widget._onDragMouseUp, true);
}

export function onDragMouseMoveHelper(widget, ev) {
  ev.stopImmediatePropagation();

  // Below the threshold: shape stays put, no onShapeMoved call — prevents a
  // click/double-click from nudging the shape a few pixels (see DRAG_THRESHOLD above).
  if (!widget._dragConfirmed) {
    const startDx = ev.clientX - widget._dragStart.x;
    const startDy = ev.clientY - widget._dragStart.y;
    if (Math.hypot(startDx, startDy) < DRAG_THRESHOLD) {
      return;
    }
    widget._dragConfirmed = true;
    // Owned by UmlCanvas.jsx (draggingShapeIdRef), NOT widget-instance state —
    // package escape/adopt mid-drag remounts this widget in the React tree,
    // which would silently wipe local state (bug found 2026-07-10, "werkt vaag").
    widget.props.draggingShapeIdRef.current = widget.props.shape.id;
  }

  const shape = widget.props.shape;
  const worldPoint = widget.props.toWorldPoint(ev.clientX, ev.clientY);
  const rawX = clampCoord(worldPoint.x - widget._dragOffset.x);
  const rawY = clampCoord(worldPoint.y - widget._dragOffset.y);
  const snapEnabled = userSettingsEngine.resolve('umlWorkspace.snapToGrid');
  const gridSize = userSettingsEngine.resolve('umlWorkspace.gridSize');
  const x = snapEnabled ? CanvasGeometry.snapToGrid(rawX, gridSize) : rawX;
  const y = snapEnabled ? CanvasGeometry.snapToGrid(rawY, gridSize) : rawY;
  const oldX = shape.x;
  const oldY = shape.y;
  if (widget.props.onShapeMoved) {
    widget.props.onShapeMoved(shape.id, x, y, { ctrlKey: ev.ctrlKey || ev.metaKey });
  }

  moveOtherSelectedShapes(widget, shape.id, x - oldX, y - oldY);
}

/**
 * Drags the rest of a multi-selection by the same delta as the shape actually
 * under the cursor — passengers follow 1:1, no independent grid-snap.
 * @param {object} widget
 * @param {string} anchorId
 * @param {number} dx
 * @param {number} dy
 */
function moveOtherSelectedShapes(widget, anchorId, dx, dy) {
  const selectedShapeIds = widget.props.selectedShapeIds;
  if (!selectedShapeIds || selectedShapeIds.size < 2 || !selectedShapeIds.has(anchorId)) {
    return;
  }
  selectedShapeIds.forEach((otherId) => {
    if (otherId === anchorId) return;
    const otherShape = widget.props.getShapeById?.(otherId);
    if (!otherShape) return;
    widget.props.onShapeMoved(otherId, otherShape.x + dx, otherShape.y + dy);
  });
}

export function onDragMouseUpHelper(widget) {
  document.body.style.userSelect = '';
  document.body.classList.remove('jx-dragging');
  document.body.style.cursor = '';
  window.removeEventListener('mousemove', widget._onDragMouseMove, true);
  window.removeEventListener('mouseup', widget._onDragMouseUp, true);

  if (widget._dragConfirmed) {
    widget.props.draggingShapeIdRef.current = null;
    // One extra forced re-render: mouseup itself doesn't call onShapeMoved, so
    // without this the drag-feedback border could stay red one frame too long
    // if the LAST mousemove landed on a freshly-remounted widget instance.
    umlCanvasEngine.notify();
  }

  // No real drag happened -> treat this as a plain click. Deliberately not
  // relying on the browser's native click/dblclick event here — both proved
  // unreliable in this widget during 2026-07-09 debugging (mousedown/mouseup
  // always fire, click/dblclick sometimes silently don't).
  if (!widget._dragConfirmed && widget.handlePlainClick) {
    widget.handlePlainClick(widget._mouseDownTarget);
  }
}
